import React, { useEffect, useRef } from 'react';
import { Trash2, Mail, Phone, Calendar, Users, ChevronDown, Check, RotateCcw, MapPin, Layers, UtensilsCrossed } from 'lucide-react';
import QuoteCategoryChips from './QuoteCategoryChips';
import { StatusBadge } from './StatusUI';

const formatDate = (s) =>
  new Date(s).toLocaleDateString('fr-FR', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });

const QuoteRow = ({ q, expanded, onToggle, onDelete, onMarkRead, onMarkProcessed, onMarkNew }) => {
  const status = q.status || 'nouveau';
  const wasNew = useRef(status === 'nouveau');

  useEffect(() => {
    // Auto mark as "lu" when this row is first expanded
    if (expanded && wasNew.current) {
      onMarkRead(q.id);
      wasNew.current = false;
    }
  }, [expanded, q.id, onMarkRead]);

  const isProcessed = status === 'traite';
  const isNew = status === 'nouveau';

  return (
    <div className={`bg-white rounded-xl shadow-sm border ${isNew ? 'border-orange-200' : 'border-gray-100'}`} data-testid={`quote-row-${q.id}`}>
      <button onClick={onToggle} className={`w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors text-left ${isNew ? 'bg-orange-50/40' : ''}`}>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <StatusBadge status={status} />
            <span className="font-semibold text-gray-900">{q.firstName} {q.lastName}</span>
            {q.eventType && (
              <span className="text-xs px-2 py-0.5 bg-orange-100 text-orange-600 rounded-full">{q.eventType}</span>
            )}
          </div>
          <div className="text-xs text-gray-500 mt-1 truncate">
            {q.email} • {q.phone} • {formatDate(q.timestamp)}
          </div>
        </div>
        <ChevronDown className={`w-4 h-4 text-gray-400 flex-shrink-0 ml-3 transition-transform ${expanded ? 'rotate-180' : ''}`} />
      </button>

      {expanded && (
        <div className="border-t border-gray-100 p-4 grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
          <div className="flex items-center gap-2">
            <Mail className="w-4 h-4 text-gray-400" />
            <a href={`mailto:${q.email}`} className="text-gray-700 hover:text-orange-600">{q.email}</a>
          </div>
          <div className="flex items-center gap-2">
            <Phone className="w-4 h-4 text-gray-400" />
            <a href={`tel:${q.phone}`} className="text-gray-700 hover:text-orange-600">{q.phone}</a>
          </div>
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-gray-400" />
            <span className="text-gray-700">{q.eventDate || 'Date non précisée'}</span>
          </div>
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-gray-400" />
            <span className="text-gray-700">{q.guests ? `${q.guests} invités` : 'Invités non précisés'}</span>
          </div>
          <div className="flex items-center gap-2">
            <MapPin className="w-4 h-4 text-gray-400" />
            <span className="text-gray-700">{q.location || 'Lieu non précisé'}</span>
          </div>
          <div className="flex items-center gap-2">
            <Layers className="w-4 h-4 text-gray-400" />
            <span className="text-gray-700">Pose : {q.surface || '—'}</span>
          </div>
          <div className="flex items-center gap-2">
            <UtensilsCrossed className="w-4 h-4 text-gray-400" />
            <span className="text-gray-700">Repas : {q.seating || '—'}</span>
          </div>
          <QuoteCategoryChips categories={q.categories} />
          {q.message && (
            <div className="md:col-span-2">
              <span className="text-xs text-gray-500 block mb-1">Message :</span>
              <p className="text-sm text-gray-700 whitespace-pre-wrap bg-gray-50 p-3 rounded-lg">{q.message}</p>
            </div>
          )}
          <div className="md:col-span-2 flex justify-between items-center pt-2 border-t border-gray-100 flex-wrap gap-2">
            <span className="text-xs text-gray-400">Newsletter : {q.newsletter ? 'Oui' : 'Non'}</span>
            <div className="flex items-center gap-2">
              {!isProcessed && (
                <button
                  onClick={() => onMarkProcessed(q.id)}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-green-500 text-white hover:bg-green-600 rounded-lg transition-colors font-medium"
                  data-testid={`mark-processed-${q.id}`}
                >
                  <Check className="w-4 h-4" /> Marquer traité
                </button>
              )}
              {isProcessed && (
                <button
                  onClick={() => onMarkNew(q.id)}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                  data-testid={`mark-new-${q.id}`}
                >
                  <RotateCcw className="w-4 h-4" /> Remettre en nouveau
                </button>
              )}
              <button
                onClick={onDelete}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                data-testid={`delete-quote-${q.id}`}
              >
                <Trash2 className="w-4 h-4" /> Supprimer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuoteRow;
